import type { TabDef } from "./types";

/** Todas as abas do sistema. A ordem define o menu lateral. */
export const TABS: TabDef[] = [
  {
    key: "colaboradores",
    label: "Colaboradores",
    icon: "users",
    resource: "colaboradores",
    subtitle:
      "Cadastro base do portal — todo colaborador nasce aqui (ou no DP) e é espelhado automaticamente para os demais sistemas. A IA sugere treinamentos (✦) e cada um tem sua pasta de certificados.",
    acoesLinha: ["ficha", "avaliacoes", "avaliar", "treinoIa", "pasta"],
    // Exportar / importar a lista em Excel (cruza pelo CPF, e-mail ou nome).
    planilha: true,
    /*
     * O recorte que o RH faz o dia inteiro: quem está no quadro HOJE.
     *
     * O cadastro guarda quem saiu (é dele que saem turnover, histórico e a
     * ficha de quem voltou), então a lista completa mistura as duas coisas —
     * hoje são 47 inativos para 38 ativos. As opções vêm do próprio campo
     * "status", não de uma lista repetida aqui.
     */
    filtros: [{ campo: "status", label: "Situação", vazioLabel: "Todas" }],
    // Cadastro de pessoas: lista em ordem alfabética (A→Z), como a agenda do
    // Planejamento. As demais abas desta tela são históricos (data primeiro).
    ordenarPorNome: "nome",
    fields: [
      { name: "nome", label: "Nome", type: "text", required: true },
      { name: "cpf", label: "CPF", type: "text" },
      { name: "email", label: "E-mail", type: "email" },
      // Cargo NÃO é digitado: abre o popup e marca na tabela de Cargos e
      // Salários (aba "cargos"), que também sugere o salário do nível.
      { name: "cargo", label: "Cargo", type: "cargo" },
      { name: "departamento", label: "Departamento", type: "text" },
      { name: "telefone", label: "Telefone", type: "text" },
      { name: "salario", label: "Salário", type: "currency" },
      { name: "data_admissao", label: "Admissão", type: "date" },
      { name: "status", label: "Status", type: "select", options: ["Ativo", "Inativo", "Afastado"] },
      { name: "skills", label: "Skills / Competências", type: "skills" },
      // ---- Ficha completa: campos que vieram da planilha do RH. Ficam só no
      // card (hideInTable) para a listagem continuar enxuta.
      { name: "data_nascimento", label: "Nascimento", type: "date", hideInTable: true },
      { name: "matricula", label: "N° registro", type: "text", hideInTable: true },
      // EMPRESA do grupo. É o que separa quem responde o formulário de
      // avaliação da SERCAMP de quem responde o da Move Me (o mesmo cargo
      // existe nas duas). Em branco, vale o que estiver no Contrato:
      // "MRN"/"PJ Morenna" = Move Me — ver src/empresas.ts.
      {
        name: "empresa",
        label: "Empresa",
        type: "select",
        options: ["SERCAMP", "Move Me"],
        hideInTable: true,
      },
      { name: "contrato", label: "Contrato (SERCAMP / NX / PJ / MRN)", type: "text", hideInTable: true },
      { name: "cbo", label: "CBO", type: "text", hideInTable: true },
      { name: "rg", label: "RG", type: "text", hideInTable: true },
      { name: "pis", label: "PIS", type: "text", hideInTable: true },
      { name: "ctps", label: "CTPS", type: "text", hideInTable: true },
      { name: "ctps_serie", label: "Série da CTPS", type: "text", hideInTable: true },
      { name: "estado_civil", label: "Estado civil", type: "text", hideInTable: true },
      { name: "nome_mae", label: "Nome da mãe", type: "text", hideInTable: true },
      { name: "nome_pai", label: "Nome do pai", type: "text", hideInTable: true },
      { name: "endereco", label: "Endereço", type: "text", hideInTable: true },
      { name: "cep", label: "CEP", type: "text", hideInTable: true },
      { name: "cidade", label: "Cidade", type: "text", hideInTable: true },
      { name: "contato_emergencia", label: "Contato de emergência", type: "text", hideInTable: true },
      { name: "parente_emergencia", label: "Parentesco (emergência)", type: "text", hideInTable: true },
      { name: "banco", label: "Banco", type: "text", hideInTable: true },
      { name: "agencia", label: "Agência", type: "text", hideInTable: true },
      { name: "conta", label: "Conta", type: "text", hideInTable: true },
      { name: "tipo_conta", label: "Tipo de conta", type: "select", options: ["Corrente", "Poupança", "Salário"], hideInTable: true },
      { name: "pix", label: "PIX", type: "text", hideInTable: true },
      { name: "data_demissao", label: "Demissão", type: "date", hideInTable: true },
    ],
  },
  {
    // TRIAGEM DE CURRÍCULO — antes das Entrevistas no menu porque é o começo do
    // funil: o candidato responde por link (WhatsApp) e o RH avalia; quem passa
    // vira entrevista. Tela própria: src/components/TriagemView.tsx.
    key: "triagem",
    label: "Triagem de Currículo",
    icon: "chat",
    resource: "triagens",
    subtitle:
      "Perguntas estratégicas por vaga (geradas por IA), link para o candidato responder pelo WhatsApp e avaliação",
    fields: [],
  },
  {
    // DISC — análise de perfil comportamental. Fica logo depois da Triagem
    // porque é o passo seguinte do mesmo funil: o RH manda o link do teste
    // para quem passou na triagem e leva o resultado para a entrevista. O
    // questionário e o gabarito são os do papel do RH (src/disc-modelo.ts) e
    // quem corrige é o servidor. Tela própria: src/components/DiscView.tsx.
    key: "disc",
    label: "DISC",
    icon: "target",
    resource: "disc",
    subtitle:
      "Teste DISC de 20 questões enviado por link (WhatsApp). O sistema corrige pelo gabarito oficial do RH, mostra o perfil (D/I/S/C) e emite o laudo com o cabeçalho da SERCAMP.",
    fields: [],
  },
  {
    key: "entrevistas",
    label: "Entrevistas",
    icon: "chat",
    resource: "entrevistas",
    subtitle:
      "Entrevistas do processo seletivo. Cada linha tem o ROTEIRO DE ENTREVISTA (prancheta): o guia completo da conversa — abertura, trajetória, técnico da função, comportamento, cultura, segurança, disponibilidade e remuneração — com anotação e nota por pergunta, parecer final e versão para imprimir. Quem fica como Aprovado ou Aprovado 1º lugar aparece para o DP (Contratações e Demissões) com o checklist da admissão; o 2º lugar fica de reserva — se o 1º desistir, troque o 2º para 'Aprovado 1º lugar'. O balão de conversa manda o RETORNO ao candidato pelo WhatsApp — positivo (com a proposta) ou negativo.",
    fields: [
      { name: "candidato", label: "Candidato", type: "text", required: true },
      { name: "cargo", label: "Cargo", type: "text" },
      { name: "data", label: "Data", type: "date" },
      { name: "horario", label: "Horário", type: "time" },
      { name: "etapa", label: "Etapa", type: "select", options: ["Triagem", "Técnica", "RH", "Gestor", "Final"] },
      { name: "entrevistador", label: "Entrevistador", type: "text" },
      // "Desistiu" e "Não compareceu" fecham a entrevista sem ser um julgamento
      // do candidato: antes só havia aprovado/reprovado, e quem sumia ficava
      // eternamente "Agendada" — a lista de chamada nunca esvaziava.
      // "Aprovado 1º/2º lugar" (pedido da Quezia, 02/10/2026): o 1º lugar é quem
      // será contratado (vai para o DP); o 2º é o reserva — se o 1º desistir, o
      // RH troca o 2º para "Aprovado 1º lugar" e não precisa refazer a busca.
      // "Aprovado" sem colocação continua (vaga com várias posições e registros
      // antigos).
      {
        name: "status",
        label: "Status",
        type: "select",
        options: [
          "Agendada",
          "Realizada",
          "Aprovado",
          "Aprovado 1º lugar",
          "Aprovado 2º lugar",
          "Reprovado",
          "Desistiu",
          "Não compareceu",
        ],
        // Cor na listagem para o desfecho saltar aos olhos ao varrer a lista.
        // Agendada/Realizada ficam em cinza: ainda não são desfecho.
        cores: {
          Aprovado: "ok",
          "Aprovado 1º lugar": "ok",
          "Aprovado 2º lugar": "azul",
          Reprovado: "danger",
          Desistiu: "warn",
          "Não compareceu": "roxo",
        },
      },
      // Fica só no card (formulário): na listagem, um texto longo ocuparia a
      // linha inteira e empurraria as colunas que o RH usa para varrer a lista.
      {
        name: "observacao",
        label: "Observação",
        type: "textarea",
        hideInTable: true,
      },
    ],
    // O CONVITE do candidato (o link com dia, hora, endereço e quem procurar na
    // recepção) vem primeiro porque é o que se faz logo depois de marcar; ao
    // lado, o RETORNO (o resultado, positivo ou negativo, pelo WhatsApp — o
    // fim da mesma conversa com o candidato); em seguida o roteiro da
    // entrevista (conduzir/registrar), o CURRÍCULO — que vem da Triagem de
    // Currículo e abre na própria tela — e a pasta de documentos entregues na
    // entrevista.
    acoesLinha: ["convite", "retorno", "roteiro", "curriculo", "documentos"],
  },
  {
    key: "contratacoes",
    label: "Contratações",
    icon: "userPlus",
    resource: "contratacoes",
    subtitle:
      "Contratações do RH — ao salvar, o colaborador já é criado automaticamente no cadastro (com cargo, departamento, salário e a data como admissão) e desce para os demais sistemas. Se já houver alguém com o mesmo nome, a contratação é ligada a essa pessoa em vez de duplicar. A contratação também aparece para o DP (Contratações e Demissões) com o checklist da admissão.",
    fields: [
      { name: "nome", label: "Nome", type: "text", required: true },
      // Mesmo popup do cadastro: o colaborador gerado já nasce com o cargo da
      // tabela de Cargos e Salários, sem grafia divergente.
      { name: "cargo", label: "Cargo", type: "cargo" },
      { name: "departamento", label: "Departamento", type: "text" },
      { name: "data", label: "Data", type: "date" },
      { name: "fonte", label: "Fonte", type: "select", options: ["LinkedIn", "Indicação", "Site", "Agência", "Outro"] },
      { name: "salario", label: "Salário", type: "currency" },
      // Quem o sistema criou/vinculou (server/contratacoes.ts) — só leitura.
      { name: "colaborador_id", label: "Colaborador", type: "text", ref: "colaboradores", soLeitura: true },
    ],
  },
  {
    // PLANO DE AÇÃO (treinamentos). Mesma lista do DP: os dois sistemas usam a
    // tabela `treinamentos` no banco `sercamp`, então nada é copiado.
    key: "treinamentos",
    label: "Plano de Ação",
    icon: "cap",
    resource: "treinamentos",
    subtitle:
      "Plano de ação e treinamento por colaborador (também disponível no DP) — anexe o certificado e use 'Concluir' para gravar a competência.",
    acoesLinha: ["certificado", "concluir"],
    fields: [
      { name: "colaborador_id", label: "Colaborador", type: "select", ref: "colaboradores", required: true },
      { name: "nome_treinamento", label: "Treinamento", type: "text", required: true },
      { name: "carga_horaria", label: "Carga horária (h)", type: "number" },
      { name: "data", label: "Data", type: "date" },
      { name: "status", label: "Status", type: "select", options: ["Planejado", "Em andamento", "Concluído"] },
      { name: "nota", label: "Nota", type: "number" },
    ],
  },
  {
    key: "engajamento",
    label: "Pesquisa de Clima",
    icon: "pulse",
    resource: "engajamento",
    subtitle:
      "Questionário oficial da SERCAMP enviado por link — um para cada colaborador. Acompanhe quem já respondeu, veja cada resposta pergunta a pergunta e o consolidado; o histórico de lançamentos continua na segunda aba (a IA sugere ações ✦).",
    acoesLinha: ["engajIa"],
    fields: [
      { name: "colaborador_id", label: "Colaborador", type: "select", ref: "colaboradores" },
      { name: "departamento", label: "Departamento", type: "text" },
      { name: "periodo", label: "Período", type: "text" },
      { name: "pontuacao", label: "Pontuação (0-100)", type: "number" },
      { name: "enps", label: "eNPS", type: "number" },
      { name: "observacao", label: "Observação", type: "textarea" },
    ],
  },
  {
    key: "demissoes",
    label: "Demissões",
    icon: "userMinus",
    resource: "demissoes",
    subtitle:
      "Registro de desligamentos — cada um aparece para o DP (Contratações e Demissões) com o checklist da demissão.",
    fields: [
      { name: "colaborador_id", label: "Colaborador", type: "select", ref: "colaboradores", required: true },
      { name: "data", label: "Data", type: "date" },
      { name: "tipo", label: "Tipo", type: "select", options: ["Voluntária", "Involuntária"] },
      { name: "motivo", label: "Motivo", type: "textarea" },
    ],
  },
  {
    // PROMOÇÕES — cada registro diz por QUAL CAMINHO a pessoa subiu: os modelos
    // (promoção vertical, progressão de nível, mérito, certificação técnica,
    // liderança, efetivação...) estão em src/promocoes-modelos.ts e aparecem
    // no botão "Modelos de promoção" da barra e no popup do formulário.
    key: "promocoes",
    label: "Promoções",
    icon: "trendUp",
    resource: "promocoes",
    subtitle:
      "Movimentações de carreira — escolha o modelo de promoção (a forma pela qual o colaborador está sendo promovido) e marque os critérios daquele caminho. Ao selecionar o colaborador, o cargo e o salário atuais já entram como 'anterior'.",
    modelosPromocao: true,
    fields: [
      { name: "colaborador_id", label: "Colaborador", type: "select", ref: "colaboradores", required: true },
      // A FORMA de promover, marcada na política — nunca digitada.
      { name: "modelo", label: "Modelo de promoção", type: "modeloPromocao", required: true },
      { name: "cargo_anterior", label: "Cargo anterior", type: "text" },
      // Mesmo popup do cadastro: o cargo de destino sai da tabela de Cargos e
      // Salários e já sugere o valor daquele nível em "Novo salário".
      { name: "cargo_novo", label: "Novo cargo", type: "cargo", preencheSalario: "salario_novo" },
      { name: "salario_anterior", label: "Salário anterior", type: "currency" },
      { name: "salario_novo", label: "Novo salário", type: "currency" },
      { name: "data", label: "Data", type: "date" },
      {
        name: "aplicar_cadastro",
        label: "Atualizar o cadastro",
        type: "select",
        options: ["Sim", "Não"],
        padrao: "Sim",
        // Sem isso a promoção seria só um papel: o colaborador continuaria com
        // o cargo antigo no cadastro (e nos outros sistemas do portal).
        // "Não" serve para promoção com data futura ou registro histórico.
      },
      // A régua do modelo escolhido: fica só no card (é uma lista de marcação).
      { name: "criterios", label: "Critérios cumpridos", type: "criteriosPromocao", hideInTable: true },
      {
        name: "justificativa",
        label: "Justificativa / parecer do gestor",
        type: "textarea",
        hideInTable: true,
      },
    ],
  },
  {
    key: "avaliacoes",
    label: "Avaliações",
    icon: "star",
    resource: "avaliacoes",
    subtitle:
      "Avaliação de desempenho pelo formulário oficial da SERCAMP (FO 094) — as perguntas são as da função do colaborador. Use 'Avaliar' para preencher aqui, ou gere um link para o gestor do setor responder. O olho abre a avaliação preenchida, questão a questão.",
    avaliacaoLink: true,
    // O olho abre TODAS as avaliações daquele colaborador (não só a da linha):
    // é assim que se compara um ciclo com o outro.
    acoesLinha: ["avaliacoes"],
    fields: [
      { name: "colaborador_id", label: "Colaborador", type: "select", ref: "colaboradores", required: true },
      { name: "periodo", label: "Período", type: "text" },
      { name: "nota", label: "Nota (0-10)", type: "number" },
      { name: "modelo_nome", label: "Formulário", type: "text", soLeitura: true },
      { name: "avaliador", label: "Avaliador", type: "text" },
      { name: "setor", label: "Setor", type: "text", hideInTable: true },
      { name: "pontos_fortes", label: "Pontos fortes", type: "textarea", hideInTable: true },
      { name: "pontos_melhoria", label: "Pontos de melhorias", type: "textarea", hideInTable: true },
      { name: "comentario", label: "Observações", type: "textarea" },
    ],
  },
  {
    // AVALIAÇÃO DE DESEMPENHO — o que a empresa FAZ com a nota: um plano de
    // ação por colaborador (ações datadas, responsável e acompanhamento).
    // Fica logo depois de "Avaliações", que é onde a nota é lançada.
    // Tela própria: src/components/AvaliacaoDesempenhoView.tsx.
    key: "desempenho",
    label: "Avaliação de Desempenho",
    icon: "trendUp",
    resource: "desempenho",
    subtitle:
      "Plano de ação por colaborador — monte a partir das avaliações (a IA sugere as ações), acompanhe os prazos e feche o ciclo.",
    fields: [],
  },
  {
    // ENDOMARKETING — comunicação interna: comunicado por WhatsApp, fotos dos
    // colaboradores, eventos do ano (pastas de imagens) e aniversariantes.
    // Tela própria: src/components/EndomarketingView.tsx.
    key: "endomarketing",
    label: "Endomarketing",
    icon: "pulse",
    resource: "endomarketing",
    subtitle:
      "Comunicado no WhatsApp, fotos dos colaboradores, eventos do ano e aniversariantes do mês",
    fields: [],
  },
  {
    key: "cargos",
    label: "Cargos e Salários",
    icon: "briefcase",
    resource: "cargos",
    subtitle:
      "Plano de Cargos e Salários 2026 — a ficha de cada cargo, as trilhas de crescimento, os critérios, a base vigente, as movimentações e o painel do ano. Só o administrador ou o responsável pelo plano alteram.",
    // Um registro por CARGO (Plano 2026, ver server/plano-cargos.ts). Os
    // campos do plano são os da planilha; o salário mínimo/médio/máximo
    // vigente NÃO é campo — é calculado na tela a partir da Base Vigente,
    // como as fórmulas da planilha. "Departamento" é o do sistema: é ele que
    // decide quem desce para a Gestão da Operação, Planejamento e Relatórios.
    fields: [
      { name: "cargo", label: "Cargo", type: "text", required: true },
      { name: "eixo", label: "Eixo (setor do plano)", type: "text", required: true },
      {
        name: "nivel",
        label: "Nível",
        type: "select",
        options: [
          "Entrada", "Auxiliar", "Auxiliar/Assistente", "Assistente", "Operacional", "Técnico",
          "Júnior", "Pleno", "Analista", "Sênior", "Especialista", "Liderança", "Supervisão",
          "Coordenação", "Gerência",
        ],
        required: true,
      },
      { name: "cbo", label: "CBO", type: "text" },
      {
        name: "departamento",
        label: "Departamento no sistema",
        type: "text",
        placeholder: "Operacional = equipe de campo",
      },
      { name: "salario", label: "Salário de referência (R$)", type: "currency" },
      { name: "missao", label: "Missão do cargo", type: "textarea" },
      { name: "atribuicoes", label: "Principais atribuições", type: "textarea" },
      { name: "escolaridade", label: "Escolaridade mínima", type: "textarea" },
      { name: "experiencia", label: "Experiência recomendada", type: "textarea" },
      { name: "conhecimentos", label: "Conhecimentos técnicos", type: "textarea" },
      { name: "competencias", label: "Competências comportamentais", type: "textarea" },
      { name: "indicadores", label: "Indicadores sugeridos", type: "textarea" },
      { name: "proximo_passo", label: "Próximo passo de carreira", type: "text" },
      { name: "ordem", label: "Ordem no plano", type: "number" },
    ],
  },
  // A aba "Usuários" foi REMOVIDA: a criação de logins é centralizada no Portal
  // SERCAMP (lá se define o usuário e quais sistemas ele pode acessar). Criar
  // login aqui gerava conta que o portão de acesso do Portal não reconhecia.
  // O acesso passa a ser sempre pelo card do Portal (SSO). Ver server/auth.ts.
  {
    /* Aba de todos os sistemas: o pedido cai na fila do Desenvolvimento. */
    key: "solicitar_atualizacao",
    label: "Solicitar Atualização",
    icon: "refresh",
    resource: "solicitar_atualizacao",
    subtitle:
      "Marque uma reunião com o Desenvolvimento, peça um ajuste ou avise um erro. O pedido vai direto para quem cuida dos sistemas, com o seu nome e a data.",
    fields: [],
  },
];

/**
 * Catálogo de competências (skills) do colaborador — a lista de marcação do
 * cadastro. Fica em ./skills-catalogo para o SERVIDOR também usar (o espelho
 * do RH/DP precisa saber quais competências são da lista padrão).
 */
export { SKILLS_CATALOGO } from "./skills-catalogo";
